import { toPng } from 'html-to-image';

import { selectors } from '@grafana/e2e-selectors';
import { config } from '@grafana/runtime';

import { isSafeDashboardPreviewImage } from './previewImage';

// Fixed output size matching the gallery card's 16:9 thumbnail container, so the
// stored image fills the card exactly regardless of the dashboard's dimensions.
const TARGET_WIDTH = 640;
const TARGET_ASPECT = 16 / 9;
const TARGET_HEIGHT = Math.round(TARGET_WIDTH / TARGET_ASPECT); // 360

// Give up if capture takes too long so a save is never blocked indefinitely.
const CAPTURE_TIMEOUT_MS = 5000;

// Capture the dashboard body rather than the whole page so the preview excludes
// app chrome and the save drawer. This is intentionally scoped to the new-layout
// dashboard body; the feature is experimental and only available with dashboard v2.
const CAPTURE_SELECTORS = [
  `[data-testid="${selectors.components.DashboardSidebarSplitter.primaryBody}"]`,
  `[data-testid="${selectors.components.DashboardSidebarSplitter.bodyContainer}"]`,
];

function findDashboardNode(): HTMLElement | null {
  for (const selector of CAPTURE_SELECTORS) {
    const node = document.querySelector<HTMLElement>(selector);
    if (node) {
      return node;
    }
  }
  return null;
}

// SECURITY / known limitation (experimental feature, gated by
// `grafana.customDashboardTemplates`, off by default):
// The captured image is a point-in-time raster of the dashboard rendered with the
// *author's* permissions, and it is stored on `spec.previewImage`, which is readable
// by everyone granted `dashboardtemplates:read` (Viewer/Editor/Admin). In orgs that
// use per-user/per-team data controls (datasource permissions, LBAC, row-level
// access), a preview can therefore surface data a given reader could not query
// themselves, bypassing the per-query authorization those features enforce. In a
// vanilla org (uniform datasource access) there is no additional exposure, since
// anyone who can open/use the template can already query the same data.
//
// Accepted as a documented limitation for the experimental phase. TODO(GA): before
// enabling this feature by default, replace the full-data capture with a data-less/
// structural preview (panel frames + titles, blanked viz) or stop storing the
// rendered image in the broadly-readable resource. (Blur is intentionally not used:
// it is reversible and preserves the sensitive shape, so it is not a real control.)

/**
 * Capture a preview image of the currently rendered dashboard as a JPEG data URI,
 * client-side (no image renderer required). The result is cropped to the card's
 * 16:9 aspect and rendered at a fixed size so it fills the gallery card container.
 *
 * Best-effort: returns undefined when the dashboard element can't be found, when
 * capture fails or times out, or when the result exceeds the size cap. Callers
 * must treat the preview as optional and fall back gracefully.
 */
export async function captureDashboardPreview(): Promise<string | undefined> {
  const node = findDashboardNode();
  if (!node) {
    return undefined;
  }

  const backgroundColor = config.theme2.colors.background.canvas;

  try {
    const width = node.clientWidth || TARGET_WIDTH;
    // Downscale by rendering at a reduced pixel ratio rather than resizing after.
    const pixelRatio = Math.min(1, TARGET_WIDTH / width);

    const dataUrl = await withTimeout(
      toPng(node, { pixelRatio, backgroundColor }).then((fullDataUrl) =>
        fullDataUrl.startsWith('data:image/') ? cropToAspect(fullDataUrl, backgroundColor) : undefined
      )
    );

    if (!isSafeDashboardPreviewImage(dataUrl)) {
      return undefined;
    }

    return dataUrl;
  } catch (error) {
    console.warn('Failed to capture dashboard template preview image', error);
    return undefined;
  }
}

function withTimeout<T>(promise: Promise<T>): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => resolve(undefined), CAPTURE_TIMEOUT_MS);
    promise.then(
      (value) => {
        clearTimeout(timeoutId);
        resolve(value);
      },
      (error) => {
        clearTimeout(timeoutId);
        reject(error);
      }
    );
  });
}

/**
 * Crop the captured dashboard to the card's 16:9 aspect ratio (top-anchored for
 * tall dashboards, horizontally centered for wide ones) and render it at a fixed
 * size, so it fills the gallery card container without letterboxing.
 */
function cropToAspect(sourceDataUrl: string, backgroundColor: string): Promise<string | undefined> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const srcW = img.naturalWidth;
        const srcH = img.naturalHeight;
        if (!srcW || !srcH) {
          resolve(undefined);
          return;
        }

        let sx = 0;
        const sy = 0;
        let sw = srcW;
        let sh = srcH;
        if (srcW / srcH > TARGET_ASPECT) {
          // Wider than 16:9: keep full height, crop width to the horizontal center.
          sw = Math.round(srcH * TARGET_ASPECT);
          sx = Math.round((srcW - sw) / 2);
        } else {
          // Taller than 16:9: keep full width, crop height anchored to the top.
          sh = Math.round(srcW / TARGET_ASPECT);
        }

        const canvas = document.createElement('canvas');
        canvas.width = TARGET_WIDTH;
        canvas.height = TARGET_HEIGHT;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(undefined);
          return;
        }

        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, TARGET_WIDTH, TARGET_HEIGHT);
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, TARGET_WIDTH, TARGET_HEIGHT);

        resolve(canvas.toDataURL('image/jpeg', 0.85));
      } catch (error) {
        reject(error);
      }
    };
    img.onerror = () => resolve(undefined);
    img.src = sourceDataUrl;
  });
}
